import React, { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, CircularProgress, Typography } from "@mui/material";

import { CommonDialog, CommonDialogActions, CommonDialogContent, CommonDialogTitle } from "../../common/dialogs";
import { fetchCompensateRisksData, setValidationErrors, createCompensation } from "../../../reducers/admin/compensationProceduresSlice";
import { CHANGE_STATUS } from "../../common/const";

import { useDispatch } from "react-redux";
import CompensateRisksDialogTable from "./compensate-risks-dialog-table";
import { nanoid } from "@reduxjs/toolkit";
import { Button } from "../../common/buttons";
import { getUpdatedFormForDeleteEntity, getUpdatedFormForEntity, getVisibleEntities } from "../../../utils/formViews";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { CompensateRisksData } from "@/types/compensation-procedures";
import { compensationProceduresService } from "../../../services/compensation-procedures-service";
import { risksService } from "../../../services/risks-service";
import checkIfRowHasMatchWithKey from "../../../utils/checkRowHasMatchWithKey";
// import { ErrorDetails } from "../../../types/common/responses/responses-types";
import "./style.less";
import { calcReport } from "../../../reducers/reports/userLevelSlice";

const pasteErrorsToRows = ({ form, setForm, validationErrors, setValidationErrors, dispatch }) => {
    const formCopy = JSON.parse(JSON.stringify(form));
    let errorIndex = null;

    const errors = Array.isArray(validationErrors) ? validationErrors : validationErrors.errors || [];

    errors.forEach((error) => {
        if (!error.field) return;

        const fieldMapping = {
            risk: "riskId",
            employee: "employeeId",
            procedure: "procedureId",
        };

        const fieldKey = fieldMapping[error.field];
        if (!fieldKey) return;

        formCopy.risks.forEach((action, index) => {
            let rowMatched = false;

            if (error.additional_info?.key) {
                rowMatched = checkIfRowHasMatchWithKey(action, error.additional_info.key, fieldMapping) as boolean;
            } else if (error.code === "blank" || error.value === "" || error.additional_info === "") {
                const fieldValue = action[fieldKey];
                rowMatched = !fieldValue || fieldValue === "" || fieldValue === null;
            } else if (error.additional_info?.value !== undefined) {
                const fieldValue = action[fieldKey];
                const errorValue = error.additional_info.value;
                rowMatched =
                    fieldValue === errorValue ||
                    (!fieldValue && !errorValue) ||
                    (fieldValue === "" && errorValue === null) ||
                    (fieldValue === null && errorValue === "");
            }

            if (rowMatched) {
                action[`error_${fieldKey}`] = error;

                if (errorIndex === null) errorIndex = index;
            }
        });
    });

    setForm(formCopy);
    dispatch(setValidationErrors(null));

    return { errorIndex };
};
const validateField = (item, error, field) => {
    if (error) {
        item[`error_${field}`] = {
            value: item[field],
            type: "validation",
            message: getValidationErrorMessage(error),
        };

        return;
    }

    item[`error_${field}`] = null;
};

const checkItemsForDuplicates = (items) => {
    const keysMap = {};
    const entityKeyFields = ["employeeId", "riskId", "procedureId"];

    const getItemKey = (i) => entityKeyFields.map((field) => i[field]).join("-");

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (!(key in keysMap)) {
            keysMap[key] = 0;
        }

        keysMap[key] += 1;
    });

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (keysMap[key] === 1) {
            entityKeyFields.forEach((field) => {
                const fieldHasError = item[`error_${field}`] && item[`error_${field}`]["code"] === "duplicate";

                if (fieldHasError) {
                    delete item[`error_${field}`];
                }
            });
        }
    });
};

const removeDuplicateError = (item) => {
    ["employeeId", "riskId", "procedureId"].forEach((field) => {
        if (item[`error_${field}`] && item[`error_${field}`].code === "duplicate") {
            item[`error_${field}`] = null;
        }
    });
};

const getValidationErrorMessage = (error) => error.detail || error.title;

type CompensateRisksDialogProps = {
    open: boolean;
    onClose: () => void;
};

const CompensateRisksDialog: FC<CompensateRisksDialogProps> = ({ open, onClose }) => {
    const intl = useIntl();
    const dispatch = useDispatch<AppDispatch>();
    const title = intl.formatMessage({ id: "compensate-risks-dialog.title" });
    const busy = useSelector((state: RootState) => state.admin.compensationProcedures.busy);
    const compensateRisksData = useSelector((state: RootState) => state.admin.compensationProcedures.compensateRisksData);
    const validationErrors = useSelector((state: RootState) => state.admin.compensationProcedures.validationErrors);
    const [form, setForm] = useState<CompensateRisksData | { risks: [] }>({ risks: [] });
    const [errorScroll, setErrorScroll] = useState<{ errorIndex: number | null } | null>(null);
    // const [formErrors, setFormErrors] = useState<Record<string, ErrorDetails> | null>(null);

    useEffect(() => {
        if (open) {
            dispatch(fetchCompensateRisksData());
        }

        return () => {
            setForm({ risks: [] });
            setErrorScroll(null);
            dispatch(setValidationErrors(null));
        };
    }, [open, dispatch]);

    useEffect(() => {
        if (compensateRisksData) {
            setForm(compensateRisksData);
        }
    }, [compensateRisksData]);

    useEffect(() => {
        if (!validationErrors) return;

        const errorScrollData = pasteErrorsToRows({
            form,
            setForm,
            validationErrors,
            setValidationErrors,
            dispatch,
        });

        // const formErrorsTmp = validationErrors.reduce((accumulator, error) => {
        //     accumulator[error.field] = error;

        //     return accumulator;
        // }, {});
        setErrorScroll(errorScrollData);
        // setFormErrors(formErrorsTmp);
    }, [validationErrors, form, dispatch]);

    const handleAddRisk = () => {
        const risk = {
            id: nanoid(),
            riskId: "",
            employeeId: "",
            procedureId: "",
            changeStatus: CHANGE_STATUS.ADD,
        };

        setForm((prevForm) => {
            if (!prevForm) return prevForm;

            return {
                ...prevForm,
                risks: [...(prevForm.risks || []), risk],
            };
        });
    };

    const handleDeleteRisk = (id) => {
        setForm((form) => {
            const { newForm } = getUpdatedFormForDeleteEntity(form, id, "risks");

            newForm.risks.forEach((item) => {
                removeDuplicateError(item);
            });

            return newForm;
        });
    };

    const handleEditRisk = async (row, field, value) => {
        switch (field) {
            case "employeeId": {
                let employee, error;
                if (value) {
                    try {
                        employee = await compensationProceduresService.getRiskEmployeeDetailed(value);
                    } catch (e) {
                        error = e;
                    }
                }
                const newEmployeeValue = employee ? employee.id : value;
                setForm((form) => {
                    const { newForm, item } = getUpdatedFormForEntity(form, row, field, newEmployeeValue, "risks", [
                        "employeeId",
                        "riskId",
                        "procedureId",
                    ]);
                    validateField(item, error, field);
                    checkItemsForDuplicates(newForm.risks)
                    return newForm;
                });
                break;
            }

            case "riskId": {
                let risk, error;
                if (value) {
                    try {
                        risk = await risksService.getRiskDetailed(value);
                    } catch (e) {
                        error = e;
                    }
                }

                const newRiskValue = risk ? risk.id : value;

                setForm((form) => {
                    const { newForm, item } = getUpdatedFormForEntity(form, row, field, newRiskValue, "risks", [
                        "employeeId",
                        "riskId",
                        "procedureId",
                    ]);
                    validateField(item, error, field);
                    validateField(item, null, "procedureId");
                    checkItemsForDuplicates(newForm.risks)
                    return newForm;
                });
                break;
            }

            case "procedureId": {
                let procedure, error;
                if (value) {
                    try {
                        procedure = await compensationProceduresService.getCompensationProcedureDetailed(value);
                    } catch (e) {
                        error = e;
                    }
                }
                const newProcedureValue = procedure ? procedure.id : value;
                setForm((form) => {
                    const { newForm, item } = getUpdatedFormForEntity(form, row, field, newProcedureValue, "risks", [
                        "employeeId",
                        "riskId",
                        "procedureId",
                    ]);
                    validateField(item, error, field);
                    checkItemsForDuplicates(newForm.risks)
                    return newForm;
                });
                break;
            }
        }
    };

    const handleSubmitForm = useCallback(async () => {
        await dispatch(createCompensation(form)).unwrap();
        await dispatch(calcReport()).unwrap();
        onClose();
    }, [dispatch, form, onClose]);

    const visibleRisks = getVisibleEntities(form?.risks);
    const risksErrorIndex = errorScroll?.errorIndex;

    return (
        <>
            <CommonDialog className="compensate-risks-dialog" open={open}>
                <CommonDialogTitle title={title} onClose={onClose} withoutCloseIcon={false} />
                <CommonDialogContent>
                    <Box display={"flex"} justifyContent={"space-between"} sx={{ paddingTop: "10px", paddingRight: "14px" }}>
                        <Typography className="compensate-risks-dialog-subtitle">
                            <FormattedMessage id="compensate-risks-dialog.subtitle" />
                        </Typography>
                        <Button
                            variant="outlined"
                            sx={{
                                color: "var(--bg-2)",
                                backgroundColor: "var(--font-2)",
                                "&:hover": {
                                    backgroundColor: "var(--font-2)",
                                },
                            }}
                            onClick={handleAddRisk}
                        >
                            <FormattedMessage id="common.btn-add" />
                        </Button>
                    </Box>
                    {busy && <CircularProgress className="report-columns-dialog-circular-progress" size={32} />}

                    <CompensateRisksDialogTable
                        risks={visibleRisks}
                        deleteRisk={handleDeleteRisk}
                        editRisk={handleEditRisk}
                        errorIndex={risksErrorIndex}
                        // validationErrors={formErrors}
                        setErrorIndex={setErrorScroll}
                    />
                    <CommonDialogActions>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                color: "var(--font-4)",
                                borderColor: "var(--font-4)",
                            }}
                        >
                            <FormattedMessage id="common.btn-cancel" />
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitForm}
                            sx={{
                                color: "var(--bg-2)",
                                backgroundColor: "var(--font-2)",
                                "&:hover": {
                                    backgroundColor: "var(--font-2)",
                                },
                            }}
                        >
                            <FormattedMessage id="compensate-risk-dialog.button-create-control" />
                        </Button>
                    </CommonDialogActions>
                </CommonDialogContent>
            </CommonDialog>
        </>
    );
};

export default CompensateRisksDialog;
